import { Component, Inject, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ModulesService } from '@shared/services/modules.service';
import { ModuleModel } from '@shared/models/entities/module.model';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AVAILABLE_ICONS } from '../available-icons';

export interface ModuleFormDialogData {
  /** Módulo a editar; ausente = crear. */
  module?: ModuleModel;
}

/**
 * Diálogo de alta/edición de un módulo.
 *
 * Antes esto era un panel inline arriba de la tabla: con el formulario
 * crecido había que scrollear toda la página para llegar a Guardar/Cancelar.
 * Como diálogo, el título queda fijo arriba, las acciones fijas abajo y solo
 * el contenido (mat-dialog-content) scrollea.
 *
 * El diálogo guarda él mismo (create/update) y cierra con `true` si hubo
 * cambios, para que la pantalla padre recargue la tabla.
 */
@Component({
  selector: 'app-module-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    FormDialogShellComponent,
  ],
  templateUrl: './module-form-dialog.component.html',
  styleUrl: './module-form-dialog.component.scss',
})
export class ModuleFormDialogComponent {
  private readonly fb                   = inject(FormBuilder);
  private readonly modulesService       = inject(ModulesService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialogRef            = inject(MatDialogRef<ModuleFormDialogComponent>);

  readonly isEditing: boolean;
  saving = signal(false);

  readonly availableIcons = AVAILABLE_ICONS;
  iconFilter = signal('');
  readonly filteredIcons = computed(() => {
    const q = this.iconFilter().trim().toLowerCase();
    if (!q) return this.availableIcons;
    return this.availableIcons.filter(i => i.toLowerCase().includes(q));
  });

  form: FormGroup = this.fb.group({
    name:        ['', [Validators.required, Validators.minLength(2)]],
    label:       ['', Validators.required],
    description: [''],
    icon:        ['dashboard', Validators.required],
    route:       ['', Validators.required],
    active:      [true],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: ModuleFormDialogData) {
    this.isEditing = !!data?.module;
    if (data?.module) {
      const m = data.module;
      this.form.patchValue({
        name:        m.name,
        label:       m.label,
        description: m.description,
        icon:        m.icon,
        route:       m.route,
        active:      m.active,
      });
    }
  }

  onIconSelectOpened(opened: boolean): void {
    if (opened) this.iconFilter.set('');
  }

  onIconFilterChange(event: Event): void {
    this.iconFilter.set((event.target as HTMLInputElement).value);
  }

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload: ModuleModel = {
      ...this.form.value,
      name: (this.form.value.name as string).trim().toUpperCase(),
    };

    this.saving.set(true);
    try {
      if (this.isEditing) {
        payload.id = this.data.module!.id;
        payload.operationType = 'single' as any;
        await this.modulesService.update(payload);
        await this.customMessageService.open({
          title: '¡Actualizado!', type: 'success',
          message: `Módulo <b>${payload.name}</b> actualizado correctamente.`,
          duration: 2500,
        });
      } else {
        await this.modulesService.create(payload);
        await this.customMessageService.open({
          title: '¡Creado!', type: 'success',
          message: `Módulo <b>${payload.name}</b> creado correctamente.`,
          duration: 2500,
        });
      }
      this.dialogRef.close(true);
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'Ocurrió un error al guardar el módulo.',
        dismissBtn: true,
      });
    } finally {
      this.saving.set(false);
    }
  }
}
